import React, { useState, useContext, useEffect } from 'react'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  CloseImg,
  ProgressBarDiv,
  ProgressBarCircle,
  ProgressBarLine,
  InputRowDiv,
  InputDiv,
  InputLabel,
  Input,
  ButtonsDiv,
  CancelButton,
  NextButtonDiv,
  NextButtonImage,
  NextButtonText,
  WarningText,
} from './style'
import StockContext from '../../../../contexts/StockContext'
import ModalContext from '../../../../contexts/ModalContext'
import UserContext from '../../../../contexts/UserContext'
import cross from '../../../../assets/addStockModal/cross.svg'
import arrowRight from '../../../../assets/addStockModal/pages/arrow-right.svg'
import {
  FieldsToHeaders,
  fieldsToHeaders,
  StockInterface,
} from '../../../../pages/ClientPanel/Stock/StockTable/utils'
import 'font-awesome/css/font-awesome.min.css'
import StockDropdown from '../StockDropdown'
import ApiService from '../../../../services/ApiService'
import { Info } from 'lucide-react'

const FirstCustomAddPage: React.FC = () => {
  const { stock, setStock, resetContext } = useContext(StockContext)
  const { setModalActive, setModalSection, itIsUpdate, stockMode, isVariation } =
    useContext(ModalContext)
  const { user } = useContext(UserContext)
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({})
  const [warningMessage, setWarningMessage] = useState('')
  const [activeMenu, setActiveMenu] = useState(0)

  const isFashion = stockMode === 'fashion'

  const requiredFields = isFashion ? ['material', 'color'] : ['material', 'format', 'family', 'density']
  const numberFields = ['density', 'runNumber']

  const getHeaderTitle = () => {
    if (isVariation) return isFashion ? 'Criar Variação de Tecido' : 'Criar Variação de Estoque'
    if (isFashion) return itIsUpdate ? 'Editar Tecido' : 'Adicionar Tecido'
    return itIsUpdate ? 'Editar Estoque' : 'Adicionar Estoque'
  }
  const headerTitle = getHeaderTitle()

  const fieldsArrayNormal: string[][] = [
    ['material', 'heatTreatment', 'format'],
    ['originCountry', 'finishing', 'supplier'],
    ['stockStreet', 'shelf', 'stockLocation'],
    ['density', 'receivedDate', 'heatNumber'],
    ['family', 'fabrication', 'description'],
    ['characteristic'],
  ]

  const isStringFieldNormal: boolean[][] = [
    [true, true, true],
    [true, true, true],
    [true, true, true],
    [false, false, true],
    [true, true, true],
    [true],
  ]

  const fieldsArrayFashion: string[][] = [
    ['material', 'format', 'originCountry'],
    ['finishing', 'supplier', 'stockStreet'],
    ['shelf', 'stockLocation', 'receivedDate'],
    ['heatNumber', 'family', 'fabrication'],
    ['description', 'characteristic', 'color'],
  ]

  const isStringFieldFashion: boolean[][] = [
    [true, true, true],
    [true, true, true],
    [true, true, false],
    [true, true, true],
    [false, false, true],
  ]

  const fieldsArray = isFashion ? fieldsArrayFashion : fieldsArrayNormal
  const isStringField = isFashion ? isStringFieldFashion : isStringFieldNormal

  const areRequiredFieldsFilled = () => {
    return requiredFields.every(
      (field) => !!stock[field as keyof StockInterface],
    )
  }

  useEffect(() => {
    const newErrors: { [key: string]: boolean } = {}
    requiredFields.forEach((field) => {
      if (!stock[field as keyof StockInterface]) {
        newErrors[field] = true
      }
    })
    setErrors(newErrors)
    setWarningMessage('')
  }, [stock, isFashion])

  const applyDateMask = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 8)
    if (digits.length <= 2) return digits
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (numberFields.includes(name) && isNaN(Number(value))) {
      setWarningMessage(name)
      return
    }
    setWarningMessage('')
    setErrors((prev) => ({
      ...prev,
      [name]: requiredFields.includes(name) && !value,
    }))
    const maskedValue = name === 'receivedDate' ? applyDateMask(value) : value
    setStock({ ...stock, [name]: maskedValue })
  }

  const fetchDensity = async (materialName: string) => {
    try {
      const response = await ApiService.get(`/backend/material/density/${materialName}`)
      const { density } = response.data
      setStock((prevStock) => ({
        ...prevStock,
        density: density.toString(),
      }))
    } catch (error) {
      console.error('Error fetching density:', error)
    }
  }

  const handleDropdownClick = (value: string, field: string) => {
    console.log('🔍 [FIRSTPAGE] Dropdown selecionado:', {
      field,
      oldValue: stock[field as keyof StockInterface],
      newValue: value
    });
    
    setStock({ ...stock, [field]: value })
    setErrors((prev) => ({
      ...prev,
      [field]: requiredFields.includes(field) && !value,
    }))

    if (field === 'color' && value) {
      setErrors((prev) => ({ ...prev, color: false }))
      setWarningMessage('')
    }

    setActiveMenu(0)

    if (!isFashion && field === 'material' && value) {
      fetchDensity(value)
    }
    
    console.log('✅ [FIRSTPAGE] Stock atualizado:', {
      field,
      newStockValue: value,
      totalFilledFields: Object.keys({...stock, [field]: value}).filter(key => stock[key as keyof StockInterface] || (key === field && value)).length
    });
  }

  const nextPage = () => {
    console.log('🚀 [FIRSTPAGE] Iniciando validação para próxima página');
    console.log('📋 [FIRSTPAGE] Estado atual do stock:', {
      material: stock.material,
      format: stock.format,
      family: stock.family,
      density: stock.density,
      totalFields: Object.keys(stock).length,
      filledFields: Object.keys(stock).filter(key => stock[key as keyof StockInterface]).length
    });
    
    const newErrors: { [key: string]: boolean } = {}
    requiredFields.forEach((field) => {
      if (!stock[field as keyof StockInterface]) {
        newErrors[field] = true
      }
    })
    if (!isFashion && isVariation && !stock.color) {
      newErrors['color'] = true
    }
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      return
    }

    setModalSection('secondCustomAdd')
  }

  return (
    <Container>
      <HeaderDiv>
        <HeaderText>{headerTitle}</HeaderText>
        <CloseDiv
          onClick={() => {
            setModalActive(false)
            resetContext()
          }}
        >
          <CloseImg src={cross} alt='Close tab' />
        </CloseDiv>
      </HeaderDiv>
      <ProgressBarDiv>
        <ProgressBarCircle className='active' />
        <ProgressBarLine />
        <ProgressBarCircle />
        <ProgressBarLine />
        <ProgressBarCircle />
      </ProgressBarDiv>
      {fieldsArray.map((inputRow, rowIndex) => (
        <InputRowDiv key={rowIndex}>
          {inputRow.map((stockField: string, colIndex) => (
            <InputDiv key={colIndex}>
              {activeMenu == rowIndex * 3 + colIndex + 1 &&
              isStringField[rowIndex][colIndex] ? (
                <StockDropdown
                  text={stock[stockField as keyof StockInterface]}
                  field={stockField}
                  openUpward={stockField === 'color'}
                  onChange={(value) => {
                    handleDropdownClick(value, stockField)
                  }}
                />
              ) : null}

              <InputLabel htmlFor={stockField}>
                {fieldsToHeaders[stockField as keyof FieldsToHeaders] || stockField}
                {requiredFields.includes(stockField) && (
                  <Info
                    size={15}
                    style={{ marginLeft: 5 }}
                    color={`${
                      !stock[stockField as keyof StockInterface]
                        ? 'red'
                        : 'gray'
                    }`}
                  />
                )}
                {!isFashion && isVariation && stockField === 'color' && !requiredFields.includes('color') && (
                  <Info
                    size={15}
                    style={{ marginLeft: 5 }}
                    color={!stock.color ? 'red' : 'gray'}
                  />
                )}
              </InputLabel>
              <Input
                placeholder={stockField === 'format' && !isFashion ? 'Selecione uma opção' : stockField === 'format' && isFashion ? 'Digite ou selecione' : stockField === 'receivedDate' ? 'DD/MM/AAAA' : 'Digite aqui'}
                name={stockField}
                value={stock[stockField as keyof StockInterface] || ''}
                readOnly={stockField === 'format' && !isFashion}
                onFocus={() => {
                  setErrors((prev) => ({ ...prev, [stockField]: false }))
                  setActiveMenu(rowIndex * 3 + colIndex + 1)
                  if (stockField === 'color') setWarningMessage('')
                }}
                onBlur={() => {
                  setErrors((prev) => ({
                    ...prev,
                    [stockField]:
                      requiredFields.includes(stockField) &&
                      !stock[stockField as keyof StockInterface],
                  }))
                  setActiveMenu(0)
                }}
                onChange={stockField === 'format' && !isFashion ? undefined : handleChange}
                onClick={() => {
                  if (stockField === 'format') {
                    setActiveMenu(rowIndex * 3 + colIndex + 1)
                  }
                }}
                autoComplete='off'
                style={{
                  cursor: stockField === 'format' && !isFashion ? 'pointer' : 'text',
                  border: errors[stockField] ? '1.5px solid red' : undefined,
                }}
              />
              {warningMessage === stockField && stockField !== 'color' && (
                <WarningText>O campo tem que ser um número!</WarningText>
              )}
              {stockField === 'color' && errors['color'] && (
                <WarningText>
                  {isFashion ? 'O campo Cor é obrigatório!' : 'O campo Cor é obrigatório para criar uma variação!'}
                </WarningText>
              )}
              {stockField === 'material' && errors['material'] && isFashion && (
                <WarningText>O campo Material é obrigatório!</WarningText>
              )}
            </InputDiv>
          ))}
        </InputRowDiv>
      ))}
      <ButtonsDiv>
        <CancelButton onClick={() => setModalSection('addStockMenu')}>
          Cancelar
        </CancelButton>
        <NextButtonDiv
          onClick={nextPage}
          style={{
            pointerEvents: areRequiredFieldsFilled() ? 'auto' : 'none',
            opacity: areRequiredFieldsFilled() ? 1 : 0.5,
          }}
        >
          <NextButtonImage src={arrowRight} alt='Right arrow icon' />
          <NextButtonText>Próximo</NextButtonText>
        </NextButtonDiv>
      </ButtonsDiv>
    </Container>
  )
}

export default FirstCustomAddPage
